/**
 * A hiánylista állapota: a betöltés és a SORFÓKUSZ.
 *
 * MIÉRT HOROG, ÉS MIÉRT A `Shell` KAPJA MEG A RÖVIDÍTÉSEIT. A súgó a
 * billentyű-regiszterből épül, „tehát nem tud elavulni" (`useShortcuts.ts`).
 * Ha ez a lista a SAJÁT `useShortcuts` hívásával jegyezné be a ↑↓-t, azok a
 * súgóból kimaradnának — a regiszter ketté hasadna, és a súgó pont attól
 * veszítené el az értékét, amiért van. Ezért a horog csak KIADJA a
 * rövidítéseit, és a `Shell` fűzi őket egy regiszterbe.
 *
 * A FÓKUSZ AZONOSÍTÓN ÜL, NEM INDEXEN. A lista tét szerint rendezett; ha egy
 * frissítés átrendezi a sorokat, egy indexre kötött fókusz MÁS sorra ugrana —
 * és a következő `Enter` más tételt kérne be. Ez a 2.6/16 bukásmódja
 * kicsiben: a fókuszkezelés nem CSS-kérdés.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/features/auth/AuthProvider';
import type { Shortcut } from '@/hooks/useShortcuts';
import { ApiError, api } from '@/lib/api-client';
import type {
  DiscrepancyList,
  DiscrepancyListGroup,
  DiscrepancyListItem,
  DiscrepancyListPartner,
} from './types';

/**
 * A betöltés kimenetei. A KÉT HIBAÁG külön van, mert a könyvelő MÁST tud
 * kezdeni velük:
 *
 *   `forbidden`  — a jogosultsága változott. Az újrapróbálás nem segít;
 *                  emberhez kell fordulnia.
 *   `failed`     — a rendszer akadt meg. Az újrapróbálás SEGÍTHET.
 *
 * A lejárt munkamenet nincs köztük, és ez szándékos: az nem a lista hibája.
 * Ott kiléptetünk, és a belépőképernyő mondja meg, miért van ott.
 *
 * Amit EGYIK ág sem tesz: nem mutatja a szerver üzenetét. Lásd a `load`-ot.
 */
export type DiscrepancyListStatus = 'loading' | 'failed' | 'forbidden' | 'ready';

/**
 * A CÉGFEJLÉC IS SOR A KURZOR SZÁMÁRA (#49).
 *
 * ⚠️ MIÉRT KELL. Az összecsukott cégnek NINCS látható tétele — ha a kurzor
 * csak tételeken járhatna, egy összecsukott céget billentyűvel soha többé nem
 * lehetne kinyitni. A fejléc tehát nem dísz: ő a csoport fogantyúja, és a
 * `J`/`K` rajta is megáll.
 *
 * Az azonosítója előtaggal megkülönböztetve. A tételek azonosítói cuid-ok,
 * tehát kettőspontot nem tartalmaznak — az ütközés kizárva.
 */
const COMPANY_ROW_PREFIX = 'company:';

export function companyRowId(companyId: string): string {
  return `${COMPANY_ROW_PREFIX}${companyId}`;
}

/** `null`, ha a sor egy TÉTEL, nem cégfejléc. */
export function companyIdOfRow(rowId: string): string | null {
  return rowId.startsWith(COMPANY_ROW_PREFIX) ? rowId.slice(COMPANY_ROW_PREFIX.length) : null;
}

/**
 * A PARTNERFEJLÉC IS SOR A KURZOR SZÁMÁRA (#77) — rajta a `Szóköz` a partner
 * összes tételét váltja. A cég azonosítója is benne van, mert a partner kulcsa
 * csak a cégen belül egyedi: ugyanaz a szállító két ügyfélnél két blokk.
 */
const PARTNER_ROW_PREFIX = 'partner:';

export function partnerRowId(companyId: string, partnerKey: string): string {
  return `${PARTNER_ROW_PREFIX}${companyId}:${partnerKey}`;
}

/** Tétel-e a sor — se cég-, se partnerfejléc. */
export function isItemRow(rowId: string): boolean {
  return !(rowId.startsWith(COMPANY_ROW_PREFIX) || rowId.startsWith(PARTNER_ROW_PREFIX));
}

/**
 * EGY CÉG SORAI, ahogy a lista mutatja — a renderelés ÉS a kurzor-sorrend
 * közös forrása. Ha a kettő külön vezetné le, a `J` más sorra lépne, mint
 * ami a képernyőn alatta áll.
 *
 * FA (#77, „B5", Viktor 2026-10-30): ügyfél → partner → számla. MINDEN
 * partner ág, az egyszámlás is — „legyen az összes számla egy szinten".
 * A `last` és a `parentLast` a fa vonalaihoz kell: az utolsó ágnál a
 * függőleges vonal véget ér, nem fut tovább a semmibe.
 *
 * ⚠️ A `partners` NÉLKÜLI VÁLASZ LAPOS MARAD. A backend és az admin külön
 * deployolható; a #77 előtti szerver nem küldi, a „B" szerver pedig a
 * várakozókat és a partner nélkülieket nem tette ágba. Ami egyik ágban sincs,
 * az a régi módon, lapos sorként áll a végén.
 */
export type GroupRow =
  | { kind: 'partner'; rowId: string; partner: DiscrepancyListPartner; last: boolean }
  | {
      kind: 'item';
      rowId: string;
      item: DiscrepancyListItem;
      /** Ág alatt áll-e — ha nem, a sor a régi, lapos alakú. */
      nested: boolean;
      last: boolean;
      parentLast: boolean;
    };

export function groupRows(group: DiscrepancyListGroup): GroupRow[] {
  const byId = new Map(group.items.map((item) => [item.id, item]));
  const partners = group.partners ?? [];
  const placed = new Set<string>();
  const rows: GroupRow[] = [];

  partners.forEach((partner, index) => {
    const parentLast = index === partners.length - 1;
    const members = partner.itemIds.flatMap((id) => byId.get(id) ?? []);
    rows.push({
      kind: 'partner',
      rowId: partnerRowId(group.company.id, partner.key),
      partner,
      last: parentLast,
    });
    members.forEach((item, at) => {
      placed.add(item.id);
      rows.push({
        kind: 'item',
        rowId: item.id,
        item,
        nested: true,
        last: at === members.length - 1,
        parentLast,
      });
    });
  });

  for (const item of group.items) {
    if (!placed.has(item.id)) {
      rows.push({
        kind: 'item',
        rowId: item.id,
        item,
        nested: false,
        last: false,
        parentLast: false,
      });
    }
  }
  return rows;
}

export interface DiscrepancyListState {
  status: DiscrepancyListStatus;
  list: DiscrepancyList | null;
  /** Újrapróbálás — csak a `failed` ágon van értelme. */
  reload: () => void;
  /** Melyik soron áll a billentyűzet-kurzor — a roving tabindex célja. */
  focusedId: string | null;
  setFocusedId: (id: string) => void;
  registerRow: (id: string, element: HTMLTableRowElement | null) => void;
  /** Össze van-e csukva ez a cég — a tételei ilyenkor nem renderelődnek. */
  isCollapsed: (companyId: string) => boolean;
  /**
   * Ki- vagy összecsukás. `force` nélkül vált; `true` csuk, `false` nyit —
   * a `H`/`L` így nem „váltó", hanem IRÁNY, ahogy a D9 kiosztása mondja.
   */
  setCollapsed: (companyId: string, collapsed?: boolean) => void;
  shortcuts: Shortcut[];
}

export function useDiscrepancyList(): DiscrepancyListState {
  const { signOut } = useAuth();
  const [list, setList] = useState<DiscrepancyList | null>(null);
  const [status, setStatus] = useState<DiscrepancyListStatus>('loading');
  const [focusedId, setFocusedId] = useState<string | null>(null);
  /**
   * AZ ÖSSZECSUKOTT CÉGEK — a munkamenet erejéig, nem tovább.
   *
   * ⚠️ NEM MENTJÜK EL (se `localStorage`, se szerver). Egy előző napról
   * megmaradt csukás pont azt a céget rejtené el, amiről a könyvelő már
   * elfelejtette, hogy becsukta — és a hiánylista lényege, hogy NE lehessen
   * elfelejteni semmit. A frissítés mindent visszanyit.
   *
   * ⚠️ ÉS NEM NYÚL A KIJELÖLÉSHEZ. A csukás nézet, nem művelet: ami ki volt
   * jelölve, kijelölve marad, és a fejléc „Bekérés — N tétel" gombja
   * változatlanul viszi. Különben egy becsukott cég némán kiesne a
   * bekérésből.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  const rows = useRef(new Map<string, HTMLTableRowElement>());
  /**
   * A futó betöltés GENERÁCIÓJA. Egy `cancelled` zászló csak a lecsatolást
   * fedné le; itt viszont a felhasználó is indíthat új kört (`reload`), és
   * akkor két kérés van a levegőben. A régebbik válasza ilyenkor NEM írhat
   * állapotot — különben egy lassú hibaválasz felülírná a friss listát.
   */
  const generation = useRef(0);

  const load = useCallback(() => {
    const mine = generation.current + 1;
    generation.current = mine;
    setStatus('loading');

    void api<DiscrepancyList>('/discrepancies')
      .then((loaded) => {
        if (mine !== generation.current) {
          return;
        }
        setList(loaded);
        setStatus('ready');
      })
      .catch((caught: unknown) => {
        if (mine !== generation.current) {
          return;
        }

        // A HIBÁT KI KELL MONDANI. Egy üres listát mutatva a képernyő azt
        // állítaná, hogy nincs bekérhető hiány — az a 2.4d „hamis nulla"
        // bukásmódja, és a könyvelő nem tudná, hogy nem hisz-e el valamit.
        //
        // DE A SZERVER ÜZENETÉT NEM MUTATJUK. Az `api-client.ts` konvenciója
        // („a szerver üzenete magyarul jön, a felület azt mutatja") a
        // BELÉPÉSRE való, ahol az üzenet a felhasználónak szól, és ő tud vele
        // mit kezdeni. Egy listalekérés hibája a RENDSZERRŐL szól: az
        // „Internal server error" se nem magyar, se nem cselekvésre váltható,
        // és rosszabb esetben a belsőségeinkről mesél. Itt a saját, rövid
        // szövegünk megy — a státuszkód dönti el, melyik.
        const status = caught instanceof ApiError ? caught.status : 0;

        if (status === 401) {
          // Nem a lista hibája: a munkamenet járt le. Kiléptetünk, és a
          // belépőképernyő mondja meg, miért került oda — enélkül a könyvelő
          // azt hiszi, ő rontott el valamit.
          signOut('expired');
          return;
        }

        // A 403 a szerepkör-ellenőrzésé, és az a FRISS adatbázisból dolgozik
        // (`roles.interceptor.ts`): egy lefokozás azonnal hat. Ilyenkor az
        // újrapróbálás nem segít, emberhez kell fordulni.
        setStatus(status === 403 ? 'forbidden' : 'failed');
      });
  }, [signOut]);

  useEffect(() => {
    load();
    // Lecsatoláskor a generációt léptetjük: a még úton lévő válasz így egy
    // már nem létező komponensre nem próbál írni.
    return () => {
      generation.current += 1;
    };
  }, [load]);

  /**
   * A sorok lapos sorrendje — a szerver rendje, CÉGFEJLÉCEKKEL együtt.
   *
   * Az összecsukott cég tételei KIMARADNAK: a `J`/`K` azon lép át, ami nem
   * látszik. Egy rejtett sorra léptetve a `focus()` nem csinálna semmit, és a
   * kurzor némán elveszne — pont az a rejtett állapot, amit a 2.6/16 kizár.
   */
  const order = useMemo(
    () =>
      list
        ? list.groups.flatMap((group) => [
            companyRowId(group.company.id),
            ...(collapsed.has(group.company.id) ? [] : groupRows(group).map((row) => row.rowId)),
          ])
        : [],
    [list, collapsed]
  );

  const registerRow = useCallback((id: string, element: HTMLTableRowElement | null) => {
    if (element) {
      rows.current.set(id, element);
    } else {
      rows.current.delete(id);
    }
  }, []);

  /**
   * A lista VÉGÉN MEGÁLL, nem fordul körbe. Egy körbeforduló lista
   * hosszan nyomva tartott nyílnál némán újrakezdi magát, és a könyvelő
   * azt hiszi, feljebb van, mint ahol tényleg áll.
   */
  /**
   * AHOL A KURZOR ÁLL — a felület szerint. Alapértelmezve az első sor, mert a
   * roving tabindex célja az; a léptetésnek ezért ebből kell indulnia.
   *
   * ⚠️ AZ ALAPÉRTELMEZÉS AZ ELSŐ TÉTEL, NEM AZ ELSŐ CÉGFEJLÉC (#49). A `Tab`
   * oda érkezzen, ahol VAN mit tenni: a fejlécen a `Space` és az `Enter` is
   * az egész cégre szól, és egy oda érkező könyvelő első leütése egy egész
   * cég kijelölését billentené át. A fejléc egy `K`-val fölötte van. Ugyanez
   * áll a PARTNERFEJLÉCRE (#77): ott a `Space` a partner összes tételét váltja.
   *
   * ⚠️ AZ ELTŰNT SOR AZONOSÍTÓJA NEM MARADHAT KURZOR. Az `order` mostantól
   * ÁLLAPOTFÜGGŐ (az összecsukott cég tételei kimaradnak), tehát a
   * `focusedId` mutathat olyan sorra, ami nincs is benne — ott az
   * `indexOf` `-1`, és a `J` ÉS a `K` egyaránt a lista tetejére ugrana. A
   * csukást ugyan a kurzor-menekítés fedi, de ez az őr attól független.
   * A munkalista horga ugyanezt csinálja (`useWorklist.ts`).
   * *(Code review, 09-18.)*
   */
  const visible = focusedId !== null && order.includes(focusedId) ? focusedId : null;
  const cursor = visible ?? order.find(isItemRow) ?? order[0] ?? null;

  const move = useCallback(
    (step: number) => {
      if (order.length === 0) {
        return;
      }
      // A LÉPTETÉS ONNAN INDUL, AHOL A KURZOR LÁTSZIK.
      //
      // A nyers `focusedId` a könyvelő első leütéséig `null`, a felület
      // viszont MÁR AZ ELSŐ SORT jelöli (a roving tabindex célja az, hogy a
      // `Tab` a listába érkezzen, ne mögé). A kettőt szétcsúsztatva az első
      // `J` vagy `↓` a `-1 → 0` lépést tenné meg — vagyis LÁTHATÓAN SEMMIT,
      // és a könyvelőnek kétszer kellene megnyomnia. Billentyűzet-első
      // felületen ez pont az a súrlódás, amiért a D9 megszületett.
      // *(A kiosztás tesztje fogta meg, 09-03.)*
      const at = cursor === null ? -1 : order.indexOf(cursor);
      const next = order[Math.min(Math.max(at + step, 0), order.length - 1)];

      setFocusedId(next);
      rows.current.get(next)?.focus();
    },
    [order, cursor]
  );

  const isCollapsed = useCallback((companyId: string) => collapsed.has(companyId), [collapsed]);

  /**
   * ⚠️ A CSUKÁS ELŐTT A KURZOR KIMENEKÜL A FEJLÉCRE.
   *
   * A kurzor a csoport egyik tételén állhat, és az a sor a csukással eltűnik
   * a DOM-ból. A böngésző a fókuszt ilyenkor a `<body>`-ra ejti: a következő
   * `J` a lap tetejéről indulna, és a könyvelő nem értené, hova lett a
   * kurzor. Ezért a csoport FEJLÉCE veszi át — az az egyetlen sor, ami
   * biztosan ott marad.
   */
  const setCollapsedFor = useCallback(
    (companyId: string, next?: boolean) => {
      const rowId = companyRowId(companyId);
      const group = list?.groups.find((candidate) => candidate.company.id === companyId);
      // A PARTNERFEJLÉC IS ELTŰNIK a csukással (#77) — rajta álló kurzort
      // ugyanúgy ki kell menekíteni, mint egy tételen állót.
      const hidden = group ? groupRows(group).map((row) => row.rowId) : [];
      // EGY FORRÁSBÓL DŐL EL, hogy csukunk-e: a frissítő és a kurzor-menekítés
      // ugyanazt a `wanted`-et nézi. Kétszer levezetve a kettő elcsúszhatna —
      // ma nem tud (egy leütés, egy render), de egy jövőbeli „mindet becsuk"
      // ciklus már igen. *(Code review, 09-18.)*
      const wanted = next ?? !collapsed.has(companyId);

      setCollapsed((current) => {
        if (wanted === current.has(companyId)) {
          return current;
        }
        const updated = new Set(current);
        if (wanted) {
          updated.add(companyId);
        } else {
          updated.delete(companyId);
        }
        return updated;
      });

      const hidesCursor = wanted && cursor !== null && hidden.includes(cursor);
      if (hidesCursor) {
        setFocusedId(rowId);
        rows.current.get(rowId)?.focus();
      }
    },
    [collapsed, cursor, list]
  );

  /**
   * A `H`/`L` A KURZOR CSOPORTJÁRA HAT, akkor is, ha a kurzor egy TÉTELEN áll
   * — nem kell előbb a fejlécre lépni. Ez a D9 szövege: „sor vagy csoport
   * ki- / összecsukása".
   */
  const companyOfCursor = useMemo(() => {
    if (cursor === null) {
      return null;
    }
    const asHeader = companyIdOfRow(cursor);
    if (asHeader !== null) {
      return asHeader;
    }
    return (
      list?.groups.find((group) => groupRows(group).some((row) => row.rowId === cursor))?.company
        .id ?? null
    );
  }, [cursor, list]);

  /**
   * A NYILAK CSAK AKKOR VANNAK ÉLESÍTVE, HA VAN MIN LÉPNI.
   *
   * A `useShortcuts` minden TALÁLATNÁL `preventDefault()`-ot hív, még mielőtt
   * a `run()` lefutna. Ha a nyilak betöltés vagy hiba alatt is be lennének
   * jegyezve, a `move()` üres listán csendben visszatérne — de a lapgörgetést
   * addigra már elvettük a billentyűzettől. Egy „nem csinál semmit, és a
   * böngészőt is megbénítja" állapot rosszabb, mint ha a rövidítés nem is
   * létezne.
   */
  const shortcuts = useMemo<Shortcut[]>(
    () =>
      order.length === 0
        ? []
        : [
            { key: 'ArrowDown', labelKey: 'shortcut.nextRow', run: () => move(1) },
            { key: 'ArrowUp', labelKey: 'shortcut.prevRow', run: () => move(-1) },
            // A `J`/`K` a D9 kiosztása, a nyilak mellett — ugyanaz a
            // levezetés, mint a munkalistán.
            { key: 'j', labelKey: 'shortcut.nextRow', run: () => move(1) },
            { key: 'k', labelKey: 'shortcut.prevRow', run: () => move(-1) },
            // A `H`/`L` IRÁNY, NEM VÁLTÓ (D9). Kétszer lenyomott `H` nem
            // nyitja vissza, amit az első becsukott — a könyvelőnek nem kell
            // fejben tartania, hogy hányadikat üti.
            ...(companyOfCursor === null
              ? []
              : [
                  {
                    key: 'h',
                    labelKey: 'shortcut.collapse',
                    run: () => setCollapsedFor(companyOfCursor, true),
                  },
                  {
                    key: 'l',
                    labelKey: 'shortcut.expand',
                    run: () => setCollapsedFor(companyOfCursor, false),
                  },
                ]),
          ],
    [move, order.length, companyOfCursor, setCollapsedFor]
  );

  return {
    status,
    list,
    reload: load,
    focusedId: cursor,
    setFocusedId,
    registerRow,
    isCollapsed,
    setCollapsed: setCollapsedFor,
    shortcuts,
  };
}
